import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Combobox } from '@/components/common/Combobox'
import { HaloUserCombobox } from '@/features/halocrm-user/HaloUserCombobox'
import { useVoivodeships } from '@/features/voivodeship/hooks'
import { useUpdateCustomerCore } from '../hooks'
import {
  updateCustomerCoreSchema,
  type UpdateCustomerCoreFormParsed,
  type UpdateCustomerCoreFormValues,
} from '../schema'
import type { Customer, UpdateCustomerCoreRequest } from '../types'

interface CustomerEditFormProps {
  customer: Customer
  onSuccess?: () => void
  onCancel?: () => void
}

export function CustomerEditForm({ customer, onSuccess, onCancel }: CustomerEditFormProps) {
  const update = useUpdateCustomerCore(customer.id)
  const { data: voivodeships } = useVoivodeships()
  const voivodeshipOptions = (voivodeships ?? []).map((v) => ({ value: v.code, label: v.labelPl }))

  const form = useForm<UpdateCustomerCoreFormValues>({
    resolver: zodResolver(updateCustomerCoreSchema),
    defaultValues: {
      type: customer.type,
      firstName: customer.firstName ?? '',
      lastName: customer.lastName ?? '',
      pesel: customer.pesel ?? '',
      name: customer.name ?? '',
      nip: customer.nip ?? '',
      regon: customer.regon ?? '',
      typeProjectCode: customer.typeProjectCode ?? '',
      voivodeshipCode: customer.voivodeshipCode ?? '',
      assigneeUserId: customer.assigneeUserId == null ? '' : String(customer.assigneeUserId),
      note: customer.note ?? '',
      isActive: customer.isActive,
    },
  })

  // Edit form keeps the original type; switching person<->company would be a separate user gesture.
  const isPerson = customer.type === 'PERSON'

  const onSubmit = async (parsed: UpdateCustomerCoreFormParsed) => {
    const payload: UpdateCustomerCoreRequest = {
      type: parsed.type,
      firstName: parsed.type === 'PERSON' ? (parsed.firstName || null) : null,
      lastName: parsed.type === 'PERSON' ? (parsed.lastName || null) : null,
      pesel: parsed.type === 'PERSON' ? (parsed.pesel || null) : null,
      name: parsed.type === 'COMPANY' ? (parsed.name || null) : null,
      nip: parsed.type === 'COMPANY' ? (parsed.nip || null) : null,
      regon: parsed.type === 'COMPANY' ? (parsed.regon || null) : null,
      typeProjectCode: parsed.typeProjectCode || null,
      voivodeshipCode: parsed.voivodeshipCode || null,
      assigneeUserId: parsed.assigneeUserId ? Number(parsed.assigneeUserId) : null,
      note: parsed.note || null,
      isActive: parsed.isActive,
    }
    try {
      await update.mutateAsync(payload)
      toast.success('Zapisano zmiany')
      onSuccess?.()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd zapisu'
      toast.error(`Nie udało się zapisać: ${msg}`)
    }
  }

  const submitting = update.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit as never)} className="flex flex-col gap-5">
        {isPerson ? (
          <>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Imię *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nazwisko *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pesel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PESEL</FormLabel>
                  <FormControl><Input {...field} inputMode="numeric" maxLength={11} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nazwa firmy *</FormLabel>
                  <FormControl><Input {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>NIP</FormLabel>
                  <FormControl><Input {...field} inputMode="numeric" maxLength={10} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="regon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>REGON</FormLabel>
                  <FormControl><Input {...field} inputMode="numeric" maxLength={14} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="typeProjectCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Typ projektu</FormLabel>
                <FormControl><Input {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="voivodeshipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Województwo</FormLabel>
                <FormControl>
                  <Combobox
                    value={field.value || null}
                    onChange={(v) => field.onChange(v ?? '')}
                    options={voivodeshipOptions}
                    emptyOptionLabel="—"
                    placeholder="Wybierz województwo..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="assigneeUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Opiekun</FormLabel>
              <FormControl>
                <HaloUserCombobox
                  value={field.value ? Number(field.value) : null}
                  onChange={(id) => field.onChange(id == null ? '' : String(id))}
                  showInactiveByDefault
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notatka</FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Anuluj
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Zapisywanie...' : 'Zapisz'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
